import React from "react";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Tag } from "lucide-react";
import { newsData } from "../data/news";

const NewsSection = () => {
  const featuredNews = newsData.filter((article) => article.featured);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getCategoryColor = (category) => {
    const colors = {
      Wellness: "bg-spa-blue-50 text-spa-blue-700 border-spa-blue-200",
      Environment: "bg-green-50 text-green-700 border-green-200",
      Events: "bg-purple-50 text-purple-700 border-purple-200",
      Health: "bg-red-50 text-red-700 border-red-200",
      Culture: "bg-amber-50 text-amber-700 border-amber-200",
    };
    return colors[category] || "bg-gray-50 text-gray-700 border-gray-200";
  };

  return (
    <section
      id="news"
      className="py-20 bg-gradient-to-br from-spa-teal-50 to-spa-blue-50"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
            NEWS
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with the latest news, events, and discoveries from
            Mankato&#39;s hot spring community
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {featuredNews.map((article, index) => (
            <motion.article
              key={article.id}
              className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden card-hover group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                {/* Actual image with gradient overlay */}
                <div className="absolute inset-0">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-spa-blue-400/30 to-spa-teal-400/30 flex items-center justify-center">
                    <div className="text-white/60 text-4xl font-serif">
                      {article.title.charAt(0)}
                    </div>
                  </div>
                </div>

                {/* Category badge */}
                <div className="absolute top-4 left-4">
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full border ${getCategoryColor(
                      article.category
                    )}`}
                  >
                    <Tag className="w-3 h-3 inline mr-1" />
                    {article.category}
                  </span>
                </div>

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(article.date)}</span>
                </div>

                <h3 className="text-lg font-serif font-semibold text-gray-800 mb-3 group-hover:text-spa-blue-600 transition-colors duration-200">
                  {article.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {article.excerpt}
                </p>

                <motion.button
                  className="flex items-center space-x-2 text-spa-blue-600 hover:text-spa-blue-700 font-medium text-sm group"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <span>Read more</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </motion.button>
              </div>
            </motion.article>
          ))}
        </div>

        {/* All News Articles - Grid */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-serif font-bold text-gray-800 mb-8 text-center">
            Recent Updates
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {newsData
              .filter((article) => !article.featured)
              .map((article, index) => (
                <motion.div
                  key={article.id}
                  className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/30 hover:bg-white/80 transition-all duration-200 group cursor-pointer"
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-lg overflow-hidden relative">
                        <img 
                          src={article.image} 
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-spa-blue-400/30 to-spa-teal-400/30 flex items-center justify-center">
                          <span className="text-white/60 text-xl font-serif font-bold">
                            {article.title.charAt(0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(
                            article.category
                          )}`}
                        >
                          {article.category}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(article.date)}
                        </span>
                      </div>

                      <h4 className="text-sm font-semibold text-gray-800 mb-2 group-hover:text-spa-blue-600 transition-colors duration-200">
                        {article.title}
                      </h4>

                      <p className="text-xs text-gray-600 leading-relaxed">
                        {article.excerpt}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </motion.div>

        {/* View All News Button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >

        </motion.div>
      </div>
    </section>
  );
};

export default NewsSection;
